import React, { useCallback, useRef, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import styles from './fields.module.css';

export interface FileUploadFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

interface FileItem {
  name: string;
  size: number;
  file: File;
}

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const [files, setFiles] = useState<FileItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const error = errors[element.name];

  const acceptedTypes = element.acceptedTypes || '*';
  const maxSize = element.maxSize || 10 * 1024 * 1024; // 10MB default
  const allowMultiple = element.allowMultiple || false;

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFiles = useCallback(
    (newFiles: FileList | null, field: any) => {
      if (!newFiles || newFiles.length === 0) return;

      const fileArray = Array.from(newFiles);
      const validFiles = fileArray.filter((file) => {
        if (file.size > maxSize) {
          console.warn(`Arquivo ${file.name} excede o tamanho máximo`);
          return false;
        }
        return true;
      });

      const newFileItems: FileItem[] = validFiles.map((file) => ({
        name: file.name,
        size: file.size,
        file,
      }));

      if (allowMultiple) {
        setFiles((prev) => [...prev, ...newFileItems]);
        field.onChange([...files.map((f) => f.file), ...validFiles]);
      } else {
        setFiles(newFileItems.slice(0, 1));
        field.onChange(validFiles[0] || null);
      }
    },
    [files, maxSize, allowMultiple]
  );

  const removeFile = (index: number, field: any) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);

    if (allowMultiple) {
      field.onChange(newFiles.map((f) => f.file));
    } else {
      field.onChange(null);
    }
  };

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={allowMultiple ? [] : null}
        render={({ field }) => (
          <>
            <div
              className={styles.fileUploadArea}
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  inputRef.current?.click();
                }
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFiles(e.dataTransfer.files, field);
              }}
            >
              <input
                ref={inputRef}
                type="file"
                accept={acceptedTypes}
                multiple={allowMultiple}
                style={{ display: 'none' }}
                onChange={(e) => handleFiles(e.target.files, field)}
                disabled={element.readOnly}
              />
              <p>Arraste arquivos aqui ou clique para selecionar</p>
              <p
                style={{
                  fontSize: '0.75rem',
                  color: '#666',
                  marginTop: '0.5rem',
                }}
              >
                Tamanho máximo: {formatSize(maxSize)}
              </p>
            </div>

            {files.length > 0 && (
              <div className={styles.fileList}>
                {files.map((file, index) => (
                  <div key={index} className={styles.fileItem}>
                    <span>
                      {file.name} ({formatSize(file.size)})
                    </span>
                    <button
                      type="button"
                      className={styles.removeFile}
                      onClick={() => removeFile(index, field)}
                      disabled={element.readOnly}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      />
      {error && (
        <p className={styles.errorMessage}>{error.message as string}</p>
      )}
    </div>
  );
};

export default FileUploadField;
